import React, { useCallback, useEffect, useMemo, useState } from "react";

import { addFollower } from "@/api/requests/addFollower";
import { FILTER_CONFIG_OPTIONS } from "@/api/requests/consumerApi/getMenuItemsV3";
import { getBrandV3Show } from "@/api/requests/getBrandV3Show";
import { getIsFollowing } from "@/api/requests/getIsFollowing";
import { removeFollower } from "@/api/requests/removeFollower";
import { Action, Category, Label } from "@/constants/events";
import { useSnackBar } from "@/context/SnackBarContext";
import { BrandDetail } from "@/custom-types/BrandDetail";
import FulfillmentMode from "@/custom-types/FulfillmentMode";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useEventTracker } from "@/hooks/useEventTracker";
import useQueryParamState from "@/hooks/useQueryParamState";
import { useSelector } from "@/redux/hooks";
import { getCoordinates } from "@/redux/selectors/location";
import { getIsLoggedIn } from "@/redux/selectors/user";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import { mapFilterObjectToAPIV2 } from "@/utils/mapUIFiltersToAPI";
import { throwOnRedirectHint } from "@/utils/redirectHints";
import { getCountryCodeHeader } from "@/utils/requestContext";

import BrandLayoutWrapper from "@/components/brands/BrandLayoutWrapper";
import DeliveryMenu from "@/components/DeliveryGateway/DeliveryMenu";
import HeaderV2 from "@/components/HeaderV2";
import useMenuFilterSearch from "@/components/MenuFilters/hooks/useMenuFilterSearch";
import { MenuPage } from "@/components/MenuFilters/types/MenuFilters";
import { useFilterQueryParams } from "@/components/MenuFilters/v2/hooks";
import MetaTags from "@/components/MetaTags";
import ShopNoResults from "@/components/ShopNoResults";
import SsoBumpoutModal from "@/components/SsoBumpoutModal";

type ShopProps = {
  brand: BrandDetail;
  isFollowing: boolean;
};

const Shop: React.FC<ShopProps> = ({ brand, isFollowing }) => {
  //prevents re-renders and extra api calls
  const permanentFilters = useMemo(
    () => ({ brand_name: [brand.name] }),
    [brand.name],
  );

  const {
    appliedFilters,
    actions: { clearAllFilters },
  } = useFilterQueryParams();
  const filtersApplied = Object.keys(appliedFilters).length > 0;

  const coordinates = useSelector(getCoordinates);

  const {
    searchMenuItems,
    results: { totalItems },
    loading,
  } = useMenuFilterSearch();

  const [fulfillmentPreference] = useQueryParamState("fulfillmentPreference");

  let fulfillmentMode: FulfillmentMode;
  if (fulfillmentPreference) {
    fulfillmentMode = fulfillmentPreference as FulfillmentMode;
  } else if (totalItems > 0) {
    fulfillmentMode = "delivery";
  } else {
    fulfillmentMode = "pickup";
  }

  // * Prefetch delivery menu item metadata to determine best fulfillment mode.
  const { lat, lon } = coordinates;
  useEffect(() => {
    searchMenuItems(lat, lon, {
      filter_config: FILTER_CONFIG_OPTIONS.BRAND_MENU,
      filters: mapFilterObjectToAPIV2({
        brand_name: brand.name,
      }),
      // * take 0 for the initial query just for metadata
      take: 50,
    });
  }, [lat, lon, brand.name]);

  const { publishEvent } = useEventTracker();
  const { addSnackBarItem } = useSnackBar();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const countryCode = useDomainCountryCode();
  const [isSsoModalVisible, setIsSsoModalVisible] = useState(false);
  const [brandFavorited, setBrandIsFavorited] = useState(isFollowing);

  const handleFavoriteClick = useCallback(async () => {
    if (brand.id) {
      if (!isLoggedIn) {
        return setIsSsoModalVisible(true);
      }
      publishEvent({
        action: Action.click,
        brandId: brand.id,
        category: Category.brandPaid,
        label: brandFavorited ? Label.unfollowButton : Label.followButton,
      });

      const followerCall = brandFavorited ? removeFollower : addFollower;

      const success = await followerCall({
        countryCode,
        followeeId: brand.id,
        followeeType: "brand",
      });

      if (success) {
        setBrandIsFavorited(!brandFavorited);
        addSnackBarItem(
          followerCall === addFollower
            ? "brandFavoriteAdded"
            : "brandFavoriteRemoved",
        );
      }
    }
  }, [brandFavorited, isLoggedIn]);

  return (
    <>
      <MetaTags
        description={`Browse ${brand.name} products near you. Order weed for pick up or same-day delivery, find reviews & menus from the top dispensaries.`}
        image={`${brand.logo}?w=1200&fm=jpg`}
        robots={"noindex"}
        title={`Shop ${brand.name}`}
      />
      <BrandLayoutWrapper brand={brand} wrapperClassName="container">
        <HeaderV2
          fulfillmentMode={fulfillmentMode}
          hideDivider
          wrapperClassName="mt-none"
          eventCategory={Category.brandPaid}
        />
        <DeliveryMenu
          brandId={brand.id}
          brandName={brand.name}
          coordinates={coordinates}
          filterConfig={FILTER_CONFIG_OPTIONS.BRAND_MENU}
          fulfillmentLoading={loading}
          isFollowing={isFollowing}
          isDeliveryFulfillmentMode={fulfillmentMode === "delivery"}
          isPickupFulfillmentMode={fulfillmentMode === "pickup"}
          permanentFilters={permanentFilters}
          showEmptyResultsMessaging={false}
          eventCategory={Category.brandPaid}
          menuPage={MenuPage.brand}
          hideCoinList
          renderNoResults={() => {
            return filtersApplied ? (
              <ShopNoResults
                action={() => clearAllFilters()}
                subtitleText="Try selecting fewer options or reset all filters"
                primaryLinkText="reset filters"
                titleText="No results found"
              />
            ) : (
              <ShopNoResults
                secondaryLink={{ href: "/shop", text: "browse all products" }}
                titleText={`No ${brand.name} products found near you`}
                subtitleText={`Follow ${brand.name} or browse all available products`}
                primaryLinkText={`follow ${brand.name}`}
                action={() => handleFavoriteClick()}
              />
            );
          }}
        />
        {isSsoModalVisible && (
          <SsoBumpoutModal
            closeModal={() => setIsSsoModalVisible(false)}
            trackSignUp={() => {
              setIsSsoModalVisible(false);
              publishEvent({
                action: Action.click,
                brandId: brand.id,
                category: Category.brandPaid,
                label: "sign-up from follow",
              });
            }}
          />
        )}
      </BrandLayoutWrapper>
    </>
  );
};

export const getServerSideProps = createGetServerSideProps<ShopProps>(
  async (context) => {
    const { slug, categorySlug } = context.query;

    if (categorySlug && categorySlug.length > 1) {
      return {
        notFound: true,
      };
    }

    const brand = await throwOnRedirectHint(
      () => getBrandV3Show(String(slug)),
      String(slug),
    );

    if (!brand) {
      return {
        notFound: true,
      };
    }

    const countryCode = getCountryCodeHeader(context.req.headers);
    const isFollowing = await getIsFollowing({
      countryCode,
      followeeId: brand.id,
      followeeType: "brand",
    });

    return {
      props: {
        brand,
        isFollowing,
      },
    };
  },
);

export default Shop;
